import { DestroyRef, effect, inject, Injectable, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MessageService, OpenCasesService } from '@core/services';
import { CaseSessionsService } from '@features/case-details/case-sessions.service';
import { HitlMessage, Message, MessageRole, PromptExample } from '@shared/models';
import { catchError, finalize, forkJoin, Observable, of, switchMap, take, tap } from 'rxjs';
import { PromptExempleService } from './prompt-exemple.service';
import { ChatInfo } from './chat';

/** What one chat holds, kept while its case is open so coming back to it costs nothing. */
export class Conversation {
  readonly messages = signal<Message[]>([]);
  readonly isThinking = signal(false);
  readonly chatLocked = signal(true);
  readonly hitlMessage = signal<HitlMessage | null>(null);
  readonly promptExamples = signal<PromptExample[]>([]);
  readonly assistantIntro = signal('');
  readonly assistantSubtext = signal('');
  readonly hideVideoCutPrompt = signal(false);
  /** What was typed and not sent, given back when the chat is shown again. */
  readonly draft = signal('');
  /** Until its messages, its lock and its suggestions have all answered. */
  readonly loading = signal(true);

  constructor(
    readonly chatId: string,
    /** Opened from a case's tab, so it ends with it. */
    readonly ofCase: boolean,
  ) {}
}

/**
 * Every conversation held open, whichever chat shows it. Fetched and heard once per chat; the hub's
 * events land in the conversation they name, on screen or not.
 */
@Injectable({ providedIn: 'root' })
export class ChatConversations {
  private readonly messageService = inject(MessageService);
  private readonly promptExempleService = inject(PromptExempleService);
  private readonly sessions = inject(CaseSessionsService);
  private readonly openCases = inject(OpenCasesService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly held = new Map<string, Conversation>();

  constructor() {
    this.handleHubEvents();

    // A closed case takes its conversation with it; the library's and a guest's belong to no tab.
    effect(() => {
      const open = new Set(this.openCases.opened().map((held) => held.id));
      untracked(() =>
        [...this.held.values()]
          .filter((conversation) => conversation.ofCase && !open.has(conversation.chatId))
          .forEach((conversation) => this.forget(conversation.chatId)),
      );
    });
  }

  /** The conversation of a chat, fetched and heard the first time it is asked for. */
  prefetch(chatId: string): Conversation {
    let conversation = this.held.get(chatId);
    if (!conversation) {
      const ofCase = this.openCases.opened().some((held) => held.id === chatId);
      conversation = new Conversation(chatId, ofCase);
      this.held.set(chatId, conversation);
      this.messageService.listen(chatId);
      this.load(conversation);
    }
    return conversation;
  }

  /** The conversation a chat shows, with what that chat says about it. */
  open(chatInfo: ChatInfo): Conversation {
    const conversation = this.prefetch(chatInfo.chatId);
    // Set on every visit so the flag follows video status updates.
    conversation.hideVideoCutPrompt.set(chatInfo.hideVideoCutPrompt ?? false);
    conversation.assistantIntro.set(chatInfo.assistantIntro ?? "I'm MIA, your virtual assistant.");
    conversation.assistantSubtext.set(chatInfo.assistantSubtext ?? 'How can I help you?');
    return conversation;
  }

  refresh(conversation: Conversation): void {
    conversation.hitlMessage.set(null);
    this.list(conversation).subscribe(() => {
      const lastMessage = conversation
        .messages()
        .filter((msg) => msg.role !== MessageRole.System)
        .at(-1);
      if (lastMessage?.hitl === true && lastMessage?.hitlMessage && lastMessage.id) {
        conversation.hitlMessage.set(lastMessage.hitlMessage);
      }
    });
  }

  send(
    conversation: Conversation,
    prompt: string,
    hitl?: { message: HitlMessage; approved: boolean },
  ): void {
    const videoId = conversation.chatId;
    const pendingId = `pending_${new Date().getTime()}`;
    if (prompt) {
      conversation.messages.update((messages) => [
        ...messages,
        { role: MessageRole.User, content: prompt, id: pendingId, videoId },
      ]);
    }
    conversation.isThinking.set(true);
    conversation.chatLocked.set(true);
    this.messageService
      .sendMessage(prompt, videoId, hitl)
      .pipe(take(1))
      .subscribe((userMessage) => {
        // Reset HITL message
        conversation.hitlMessage.set(null);
        // update message list with user message id
        conversation.messages.update((messages) => {
          const messageSent = messages.find((m) => m.id === pendingId);
          if (messageSent) {
            messageSent.id = userMessage.id;
          }
          return messages;
        });
      });
  }

  clear(conversation: Conversation): Observable<Message[]> {
    conversation.hitlMessage.set(null);
    return this.messageService
      .deleteChatHistoryForVideo(conversation.chatId)
      .pipe(switchMap(() => this.list(conversation)));
  }

  private load(conversation: Conversation): void {
    const history = this.list(conversation).pipe(
      tap(() => {
        const lastMessage = conversation
          .messages()
          .filter((msg) => msg.role !== MessageRole.System)
          .at(-1);
        if (lastMessage?.hitl === true && lastMessage?.hitlMessage && lastMessage.id) {
          conversation.hitlMessage.set(lastMessage.hitlMessage);
        }
      }),
    );
    const lock = this.messageService
      .getChatLocked(conversation.chatId)
      .pipe(tap((chatLocked) => conversation.chatLocked.set(chatLocked)));
    const suggestions = this.promptExempleService
      .promptExemples(conversation.chatId)
      .pipe(tap((prompts) => conversation.promptExamples.set(prompts)));
    // A call that fails still ends the wait: the chat shows what it has.
    const settled = <T>(call: Observable<T>) => call.pipe(catchError(() => of(null)));
    forkJoin([settled(history), settled(lock), settled(suggestions)])
      .pipe(finalize(() => conversation.loading.set(false)))
      .subscribe();
  }

  private list(conversation: Conversation): Observable<Message[]> {
    return this.messageService.list(conversation.chatId).pipe(
      take(1),
      tap((messages) => conversation.messages.set(messages)),
    );
  }

  private forget(chatId: string): void {
    if (this.held.delete(chatId)) {
      this.messageService.unlisten(chatId);
    }
  }

  private handleHubEvents(): void {
    const held = (chatId: string) => this.held.get(chatId);
    const hub = this.messageService;

    hub.onPartial$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ chatId, payload: message }) => {
        held(chatId)?.messages.update((messages) => {
          const lastMessage = messages.find((m) => m.id === message.id);
          if (lastMessage) {
            lastMessage.content += message.content;
          } else {
            messages.push(message);
          }
          return [...messages];
        });
      });

    hub.onComplete$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ chatId, payload: message }) => {
        const conversation = held(chatId);
        if (!conversation) {
          return;
        }
        this.goToIfOneTime(chatId, message.content);
        conversation.isThinking.set(false);
        conversation.messages.update((messages) => {
          const lastMessage = messages.find((m) => m.id === message.id);
          if (lastMessage) {
            lastMessage.content = message.content;
          } else {
            messages.push(message);
          }
          return [...messages];
        });
      });

    hub.onChatLocked$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ chatId, payload }) => held(chatId)?.chatLocked.set(payload.locked));

    hub.onHitl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(({ chatId, payload }) => {
      const conversation = held(chatId);
      conversation?.hitlMessage.set(payload);
      conversation?.isThinking.set(false);
    });

    hub.onUserMessage$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ chatId, payload: message }) => {
        const conversation = held(chatId);
        if (!conversation || !message) return;

        if (message.content) {
          const pendingId = `pending_${new Date().getTime()}`;
          conversation.messages.update((messages) => [
            ...messages,
            { role: MessageRole.User, content: message.content, id: pendingId, videoId: chatId },
          ]);
          conversation.isThinking.set(true);
          conversation.chatLocked.set(true);
        }

        if (message.hitl === false) {
          conversation.isThinking.set(true);
          conversation.chatLocked.set(true);
          conversation.hitlMessage.set(null);
        }
      });

    hub.onUserMessageDeleted$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(({ chatId }) => {
          const conversation = held(chatId);
          if (!conversation) {
            return of([]);
          }
          conversation.hitlMessage.set(null);
          return this.list(conversation);
        }),
      )
      .subscribe();
  }

  private goToIfOneTime(chatId: string, messageContent: string): void {
    const session = this.sessions.forChat(chatId);
    if (!session) {
      return;
    }
    // Accept ts as mm:ss or hh:mm:ss and always emit hh:mm:ss
    const matches = [...messageContent.matchAll(/\?ts=(?:(\d{2}):)?(\d{2}):(\d{2})/g)];
    const timestamps = matches.map((m) => {
      const hh = (m[1] ?? '00').toString().padStart(2, '0');
      const mm = m[2];
      const ss = m[3];
      return `${hh}:${mm}:${ss}`;
    });
    const [timestamp] = timestamps;
    if (timestamps.length === 1 && timestamp) {
      session.clickOnTimeLink(timestamp);
    }
  }
}
